import { useEffect, useMemo, useState } from 'react';
import { Button, Spin, Table, Tag, Space, Input, Tooltip } from 'antd';
import { Tabs as AntTabs } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography, Stack, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import GavelIcon from '@mui/icons-material/Gavel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import IdcardOutlined from '@ant-design/icons/IdcardOutlined';
import CreditCardOutlined from '@ant-design/icons/CreditCardOutlined';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import UserAddOutlined from '@ant-design/icons/UserAddOutlined';
import UserDeleteOutlined from '@ant-design/icons/UserDeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import SafetyCertificateOutlined from '@ant-design/icons/SafetyCertificateOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import Swal from 'sweetalert2';
import axios from 'axios';
import api from '../../../Api/axiosInstance';
import TableauDossiers, { FiltreTableau } from '../../TableauDossiers';
import SectionCard from './SectionCard';
import KycDocumentsPanel from './KycDocumentsPanel';
import KycImageThumb, { LONGUEUR_MIN_DATA_URI } from './KycImageThumb';
import { getVal } from './KycClientHeader';
import ConfirmPasswordModal from '../../ConfirmPasswordModal';
import Data from '../../../Data/Data.json';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import { Crypter } from '../../../Functions/Security/Crypter';
import Hash from '../../../Functions/Security/Hash';
import {
  CHAMPS_DOCUMENT_IDENTITE_KYC,
  CHAMPS_IDENTITE_PROCURATION,
  CHAMPS_IDENTITE_SIGNATAIRE,
  CHAMPS_IMAGE_IDENTITE_KYC,
  CHAMPS_IMAGE_KYC,
  CHAMPS_PAR_TYPE_MODIFICATION,
  CHAMPS_SELECT_KYC,
  LABELS_CHAMP_IDENTITE_KYC,
  LABELS_CHAMP_KYC,
  TYPES_MODIFICATION_KYC,
  TYPES_MODIFICATION_RELATION_KYC,
  type ChampKyc,
  type DemandeDetailChampRow,
  type DemandeDetailRelationRow,
  type DemandeEnAttenteRow,
  type DemandeKycValidationDto,
  type ProcurationCompteRow,
  type RelationClientRow,
  type SignataireCompteRow,
  type StatutValidationKyc,
} from '../../../Models/DemandeKyc';

const STATUT_DEMANDE_LABELS: Record<string, string> = {
  EN_ATTENTE: 'En attente',
  VALIDE: 'Validée',
  REJETE: 'Rejetée',
};

// Filtres Select de la barre d'outils TableauDossiers (LibelleStatut/LibelleType
// sont injectés dans chaque ligne — cf. demandesAffichees)
const FILTRES_DEMANDES: FiltreTableau[] = [
  { champ: 'LibelleStatut', label: 'Statut' },
  { champ: 'TypeModification', label: 'Type de demande', champLib: 'LibelleType' },
];

// Par défaut le filtre Statut affiche les demandes « En attente » (comme avant
// le Segmented) — les autres statuts restent accessibles sans rechargement.
const FILTRES_DEFAUT: Record<string, string> = { LibelleStatut: STATUT_DEMANDE_LABELS.EN_ATTENTE };

const TYPE_MODIFICATION_LABELS: Record<string, string> = {
  ...Object.fromEntries(TYPES_MODIFICATION_KYC.map((t) => [t.value, t.label])),
  ...Object.fromEntries(TYPES_MODIFICATION_RELATION_KYC.map((t) => [t.value, t.label])),
};


// Champs "compte" (le reste — identité, téléphone du client, coordonnées,
// pièce d'identité — est un champ "client") : nom du compte (2026-09-28 :
// gardé ici uniquement pour l'affichage des demandes déjà soumises avant
// que ce champ ne devienne non modifiable), la formule ET/OU entre
// signataires (2026-09-25, SIGN1-5/TSIGN1-4), le téléphone du compte
// (2026-09-28, TELMOBIL — distinct de TELCLI/TEL2), et l'identité propre
// d'un signataire/mandataire (2026-10-08, préfixes REL_/PROC_) — ça ne
// concerne pas le compte à proprement parler, mais s'affiche avec le reste
// des infos "relation" dans le même onglet Compte (avec relationInfo).
const COMPTE_CHAMPS = new Set<string>([
  ...CHAMPS_PAR_TYPE_MODIFICATION.NOM_COMPTE,
  ...CHAMPS_PAR_TYPE_MODIFICATION.TYPE_SIGNATURE,
  ...CHAMPS_PAR_TYPE_MODIFICATION.TELEPHONE_COMPTE,
  ...CHAMPS_IDENTITE_SIGNATAIRE,
  ...CHAMPS_IDENTITE_PROCURATION,
]);

const RELATION_ACTION_INFO: Record<string, { label: string; color: string; kind: 'ajout' | 'retrait' | 'modification' }> = {
  SIGNATAIRE_AJOUT: { label: "Ajout d'un signataire", color: 'green', kind: 'ajout' },
  SIGNATAIRE_RETRAIT: { label: "Retrait d'un signataire", color: 'red', kind: 'retrait' },
  PROCURATION_AJOUT: { label: "Ajout d'une procuration", color: 'green', kind: 'ajout' },
  PROCURATION_RETRAIT: { label: "Retrait d'une procuration", color: 'red', kind: 'retrait' },
  SIGNATAIRE_MODIFICATION: { label: "Modification de l'identité d'un signataire", color: 'blue', kind: 'modification' },
  PROCURATION_MODIFICATION: { label: "Modification de l'identité d'un mandataire", color: 'blue', kind: 'modification' },
};

const infoTile = (label: string, value: any) => (
  <Grid item xs={6} sm={4} md={3}>
    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.3, fontSize: 11 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>{value ?? '—'}</Typography>
    </Box>
  </Grid>
);

const infoTileImage = (label: string, src: string | undefined) => (
  <Grid item xs={6} sm={4} md={3}>
    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.3, fontSize: 11 }}>
        {label}
      </Typography>
      <KycImageThumb src={src} alt={label} size={50} />
    </Box>
  </Grid>
);

interface ValidationPanelProps {
  // Restreint la liste "Demandes" à ces TypeModification (ex. la page dédiée
  // "Validation identité signataire/procuration") — non fourni, aucun filtre
  // (comportement inchangé sur /kyc/validation : toutes les demandes KYC).
  typesModification?: string[];
  // Texte affiché sous le titre "Demandes" à la place du libellé par défaut.
  sousTitre?: string;
}

// Même structure que src/Components/Forms/Financement/Ijarah/ValidationDocument :
// une sélection en un clic (radio) charge directement le détail dans les onglets
// au-dessus de la liste, et le panneau Actions (.bG) porte Valider/Rejeter/Annuler.
export default function ValidationPanel({ typesModification, sousTitre }: ValidationPanelProps = {}) {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  // Page dédiée "Validation identité signataire/procuration" : ces demandes
  // n'ont ni champs client ni NUMCPT (DemandeKycIdentiteDto n'en porte pas),
  // donc les onglets "Client"/"Compte" (et les tableaux "actuels du compte",
  // toujours vides sans NUMCPT) n'ont pas de sens ici — remplacés par un seul
  // onglet "Détails" (même contenu utile : diff des champs + action demandée).
  const modeIdentite = Boolean(typesModification?.length);

  const [busy, setBusy] = useState(false);
  const [subTab, setSubTab] = useState('0');

  const [demandes, setDemandes] = useState<DemandeEnAttenteRow[]>([]);
  const [detailsChamp, setDetailsChamp] = useState<DemandeDetailChampRow[]>([]);
  const [detailsRelation, setDetailsRelation] = useState<DemandeDetailRelationRow[]>([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selected, setSelected] = useState<DemandeEnAttenteRow | null>(null);
  // Commentaire : de nouveau un onglet dédié (5 onglets, comme avant la
  // modale tout-en-un) — rédigé à l'avance, envoyé avec la décision.
  const [commentaire, setCommentaire] = useState('');
  // Valider/Rejeter ouvrent la reconfirmation de mot de passe (composant
  // partagé ConfirmPasswordModal) avant de soumettre réellement la décision.
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<StatutValidationKyc | null>(null);
  // Aperçu d'une pièce jointe (PROC_PIECEJ1/2) — même modale que
  // KycDocumentsPanel/EditableValueField (iframe + bouton Télécharger), pas
  // un lien qui ouvre un nouvel onglet brut.
  const [previewDocument, setPreviewDocument] = useState<{ label: string; uri: string } | null>(null);
  // Cache code -> libellé par champ picklist (ex. CLARIS: {B: 'ASSEZ BON RISQUE'}) —
  // chargé à la demande, une seule fois par champ rencontré, jamais réenvoyé
  // par le backend avec les demandes (celles-ci ne stockent que le code).
  const [libellesParChamp, setLibellesParChamp] = useState<Record<string, Record<string, string>>>({});
  // Contexte pour les opérateurs "Type de signature" (TSIGN1-3) : qui occupe
  // chaque emplacement SIGN1-4 au moment de la validation (pas dans la
  // demande elle-même, qui ne porte que l'opérateur modifié) — chargé à la
  // demande seulement si la demande sélectionnée touche un TSIGNx.
  const [compteActuel, setCompteActuel] = useState<any | null>(null);
  const [relationsClientActuel, setRelationsClientActuel] = useState<RelationClientRow[]>([]);
  // Fiche client de la demande sélectionnée — uniquement pour afficher le
  // badge "Modifié X fois" (NbModificationsKyc), pas besoin d'un onglet dédié.
  const [clientActuel, setClientActuel] = useState<any | null>(null);
  // Contexte pour un ajout/retrait signataire ou procuration : la demande ne
  // porte que la fiche concernée par l'action — on va chercher la liste
  // complète des signataires/procurations déjà en place sur le compte, pour
  // que le validateur voie qui est déjà autorisé avant de décider.
  const [signatairesActuelsCompte, setSignatairesActuelsCompte] = useState<SignataireCompteRow[]>([]);
  const [procurationsActuelsCompte, setProcurationsActuelsCompte] = useState<ProcurationCompteRow[]>([]);

  // statut=ALL : toutes les demandes sont chargées d'un coup ; le statut est
  // ensuite un filtre client de la barre d'outils (comme les écrans financement)
  async function loadDemandes() {
    setBusy(true);
    try {
      const response = await api.get(`${Data.ApiUrl.KycDemandesEnAttente}?statut=ALL`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const decoded = Crypter.decrypt(response.data);
      const parsed = JSON.parse(decoded);
      const payload = parsed?.Value ?? parsed;

      if (payload?.StatutCode === 200 && payload.Result) {
        setDemandes(payload.Result.Table ?? []);
        setDetailsChamp(payload.Result.Table1 ?? []);
        setDetailsRelation(payload.Result.Table2 ?? []);
      } else {
        setDemandes([]);
        setDetailsChamp([]);
        setDetailsRelation([]);
      }
    } catch {
      setDemandes([]);
      setDetailsChamp([]);
      setDetailsRelation([]);
      Swal.fire({ title: 'Erreur', text: 'Impossible de récupérer les demandes.', icon: 'error' });
    } finally {
      setBusy(false);
      setSelected(null);
      setSelectedRowKeys([]);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDemandes(); }, []);

  // Un seul clic sur le radio charge directement le détail dans les onglets —
  // pas de bouton "Traiter" séparé.
  function onSelectChange(newSelectedRowKeys: React.Key[]) {
    setSelectedRowKeys(newSelectedRowKeys);
    const key = newSelectedRowKeys[0];
    const row = demandes.find((d) => String(d.IdDemande) === String(key));
    setSelected(row ?? null);
    setCommentaire('');
    // Un clic sur une demande bascule directement vers l'onglet "Client".
    setSubTab('1');
  }

  const rowSelection = { selectedRowKeys, onChange: onSelectChange };

  // Filtre par type (le backend ne distingue pas par TypeModification dans
  // GET /Kyc/DemandesEnAttente) — utilisé par la page dédiée "Validation
  // identité signataire/procuration" pour ne montrer que ces demandes.
  // Chaque ligne est enrichie de LibelleType (option du filtre « Type ») et de
  // CodRel (retrouvé via detailsRelation/Table2, ex. signataire/procuration en
  // mode identité) : la recherche globale de TableauDossiers les couvre alors.
  const demandesAffichees = useMemo(() => {
    const codRelParDemande = new Map(
      detailsRelation.map((d) => [String(d.IdDemande), d.CodRel ?? d.NumRelExistant ?? '']),
    );
    const liste = typesModification ? demandes.filter((d) => typesModification.includes(d.TypeModification)) : demandes;
    return liste.map((d) => ({
      ...d,
      CodRel: codRelParDemande.get(String(d.IdDemande)) ?? '',
      LibelleType: TYPE_MODIFICATION_LABELS[d.TypeModification] ?? d.TypeModification ?? '',
      LibelleStatut: STATUT_DEMANDE_LABELS[d.StatutValidation ?? ''] ?? d.StatutValidation ?? '',
    }));
  }, [demandes, typesModification, detailsRelation]);

  function handleAnnuler() {
    setSelected(null);
    setSelectedRowKeys([]);
    setCommentaire('');
    setPendingDecision(null);
    setShowPasswordConfirm(false);
    setSubTab('0');
  }

  async function submitDecision(decision: StatutValidationKyc, motif?: string) {
    if (!selected) return;

    const dto: DemandeKycValidationDto = {
      IdDemande: selected.IdDemande,
      StatutValidation: decision,
      CommentaireValidation: motif || undefined,
      ValidePar: user.coduti,
    };

    setBusy(true);
    try {
      const response = await api.post(Data.ApiUrl.KycValider, Crypter.encrypt(JSON.stringify(dto)), {
        headers: { 'Content-Type': 'application/json' },
      });
      const decoded = Crypter.decrypt(response.data);
      const parsed = JSON.parse(decoded);
      const payload = parsed?.Value ?? parsed;

      if (payload?.StatutCode === 200) {
        Swal.fire({ title: 'Succès', text: payload?.Message ?? 'Décision enregistrée.', icon: 'success' });
        handleAnnuler();
        await loadDemandes();
      } else {
        Swal.fire({ title: 'Erreur', text: payload?.Message ?? 'La décision a été refusée.', icon: 'error' });
      }
    } catch {
      Swal.fire({ title: 'Erreur', text: "Impossible d'enregistrer la décision.", icon: 'error' });
    } finally {
      setBusy(false);
    }
  }

  // Valider/Rejeter déclenchent la reconfirmation du mot de passe — le
  // commentaire, lui, est déjà rédigé dans son propre onglet au moment du clic.
  function handleValidate() {
    if (!selected) return;
    setPendingDecision('VALIDE');
    setShowPasswordConfirm(true);
  }

  function handleReject() {
    if (!selected) return;
    if (!commentaire.trim()) {
      Swal.fire({ title: 'Motif requis', text: "Veuillez saisir un motif dans l'onglet « Commentaire » avant de rejeter.", icon: 'warning' });
      setSubTab('5');
      return;
    }
    setPendingDecision('REJETE');
    setShowPasswordConfirm(true);
  }

  // Vérifie le mot de passe via la route dédiée (ne modifie rien, pas d'effet
  // de bord) — même convention que UpdatePassword.tsx : Hash() du mot de
  // passe, payload chiffré dans l'en-tête CipherUser.
  async function verifierMotDePasse(motDePasse: string): Promise<boolean> {
    try {
      const payload = { coduti: user.coduti, motpas: Hash(motDePasse) };
      const response = await axios.post(Data.ApiUrl.verifyPassword, undefined, {
        headers: { CipherUser: Crypter.encrypt(JSON.stringify(payload)), Authorization: Data.SecretKey.ApiToken },
      });
      const res = JSON.parse(Crypter.decrypt(response.data)).Value;
      return res?.StatutCode === 200;
    } catch {
      return false;
    }
  }

  async function onConfirmPassword(motDePasse: string): Promise<boolean> {
    const ok = await verifierMotDePasse(motDePasse);
    if (ok && pendingDecision) {
      setShowPasswordConfirm(false);
      submitDecision(pendingDecision, commentaire);
    }
    return ok;
  }

  // Maker-checker : on compare « Enregistré par » de la demande au code de
  // l'utilisateur connecté — celui qui a saisi une demande ne peut pas la
  // valider/rejeter lui-même (contrôle aussi fait côté base, sp_ValiderDemandeKyc).
  const MSG_PROPRE_DEMANDE = "Vous avez enregistré cette demande : un autre utilisateur doit la valider ou la rejeter.";
  const estMaDemande = (row?: { EnregistrePar?: string | null; StatutValidation?: string | null } | null) =>
    !!row
    && String(row.EnregistrePar ?? '').trim() !== ''
    && String(row.EnregistrePar ?? '').trim() === String(user.coduti ?? '').trim()
    && (!row.StatutValidation || row.StatutValidation === 'EN_ATTENTE');

  // « 2026-10-29T09:28:51 » → « 29/10/2026 09:28 » (lisible, et plus court).
  const formatDateHeure = (v?: string | null) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  };

  // Largeurs fixes : sans elles, le tableau (layout fixed) répartit la place
  // à parts égales et tronque Client/Type/Date. Le défilement horizontal
  // (scroll.x ci-dessous) prend le relais si l'écran est trop étroit.
  const demandeColumns = [
    { title: 'Référence', dataIndex: 'IdDemande', key: 'IdDemande', width: 100 },
    // Mode identité : ces demandes n'ont ni NUMCPT (toujours vide) ni de lien
    // utile avec le client affiché (NUMCLI/NOMCLI = juste le propriétaire de
    // la fiche relation, pas ce que le validateur regarde) — remplacés par le
    // signataire/mandataire concerné (Table2, déjà résolu par le backend).
    ...(modeIdentite ? [{
      title: 'Signataire / Mandataire', key: 'personneRelation', width: 260,
      render: (_: any, row: DemandeEnAttenteRow) => {
        const rel = detailsRelation.find((d) => String(d.IdDemande) === String(row.IdDemande));
        const nom = [rel?.NomRel, rel?.Prenom].filter(Boolean).join(' ');
        return nom || rel?.CodRel || rel?.NumRelExistant || '—';
      },
    }] : [
      { title: 'Client', dataIndex: 'NOMCLI', key: 'NOMCLI', width: 260, ellipsis: true, render: (v: string, row: DemandeEnAttenteRow) => `${row.NUMCLI} — ${v}` },
      { title: 'Compte', dataIndex: 'NUMCPT', key: 'NUMCPT', width: 200 },
    ]),
    {
      title: 'Type', dataIndex: 'TypeModification', key: 'TypeModification', width: 300,
      render: (v: string) => <Tag color="blue" style={{ marginInlineEnd: 0 }}>{TYPE_MODIFICATION_LABELS[v] ?? v}</Tag>,
    },
    {
      title: 'Enregistré par', dataIndex: 'EnregistrePar', key: 'EnregistrePar', width: 200,
      render: (v: string, row: DemandeEnAttenteRow) => estMaDemande(row)
        ? (
          <Tooltip title={MSG_PROPRE_DEMANDE}>
            <span>{v} <Tag color="gold" style={{ marginLeft: 4 }}>Votre demande</Tag></span>
          </Tooltip>
        )
        : v,
    },
    { title: 'Date', dataIndex: 'DateCreation', key: 'DateCreation', width: 150, render: (v: string) => formatDateHeure(v) },
    {
      title: 'Statut', dataIndex: 'StatutValidation', key: 'StatutValidation', width: 120,
      render: (v: string | null) => !v || v === 'EN_ATTENTE'
        ? <Tag color="orange">En attente</Tag>
        : <Tag color={v === 'VALIDE' ? 'green' : 'red'}>{v === 'VALIDE' ? 'Validée' : 'Rejetée'}</Tag>,
    },
    { title: 'Décidé par', dataIndex: 'ValidePar', key: 'ValidePar', width: 130 },
    { title: 'Date décision', dataIndex: 'DateValidation', key: 'DateValidation', width: 160, render: (v: string) => formatDateHeure(v) },
    {
      title: 'Commentaire', dataIndex: 'CommentaireValidation', key: 'CommentaireValidation', width: 240, ellipsis: true,
      render: (v: string | null) => v || '—',
    },
  ];

  const detailsChampSelected = selected ? detailsChamp.filter((d) => String(d.IdDemande) === String(selected.IdDemande)) : [];
  const detailsRelationSelected = selected ? detailsRelation.filter((d) => String(d.IdDemande) === String(selected.IdDemande)) : [];
  // Le backend renvoie parfois StatutValidation="EN_ATTENTE" (chaîne non
  // vide) plutôt que null pour une demande non traitée — Boolean(...) seul
  // la considérerait à tort comme déjà décidée. On ne considère "décidée"
  // que VALIDE ou REJETE explicitement.
  const dejaDecidee = selected?.StatutValidation === 'VALIDE' || selected?.StatutValidation === 'REJETE';

  // PHOTO/SIGNATURE s'affichent désormais comme des lignes normales dans le
  // tableau Client (champTable/renderValeurChamp savent déjà les rendre en
  // aperçu image) — plus d'onglet séparé pour ça.
  const champsClientSelected = detailsChampSelected.filter((d) => !COMPTE_CHAMPS.has(d.Champ));
  const champsCompteSelected = detailsChampSelected.filter((d) => COMPTE_CHAMPS.has(d.Champ) && !CHAMPS_IMAGE_KYC.includes(d.Champ as ChampKyc));
  const relationSelectedRow = detailsRelationSelected[0];
  // Une demande combinée (MODIFICATION_CLIENT_COMPTE) porte le type d'action
  // relation sur la ligne de détail elle-même (TypeRelation), pas dans l'en-
  // tête de la demande — repli sur l'en-tête pour les anciennes demandes
  // signataire/procuration seules, créées avant cette évolution backend.
  const relationInfo = relationSelectedRow?.TypeRelation
    ? RELATION_ACTION_INFO[relationSelectedRow.TypeRelation]
    : (selected ? RELATION_ACTION_INFO[selected.TypeModification] : undefined);

  // Les demandes ne stockent que le CODE des champs picklist (CLARIS='B'...),
  // jamais leur libellé — on va chercher la liste complète de la rubrique
  // concernée (même config que le picklist lui-même) une seule fois par champ
  // rencontré, et on construit un dictionnaire code -> libellé à partir de là.
  useEffect(() => {
    const champsPicklistPresents = Array.from(new Set(detailsChampSelected.map((d) => d.Champ)))
      .filter((champ) => CHAMPS_SELECT_KYC[champ as ChampKyc] && !libellesParChamp[champ]);
    champsPicklistPresents.forEach((champ) => {
      const params = CHAMPS_SELECT_KYC[champ as ChampKyc]!;
      axios.get(Data.ApiUrl.AnySearch, {
        headers: { CypherSearch: Crypter.encrypt(JSON.stringify({ ...params, Init: '' })), 'Content-Type': 'application/json' },
        withCredentials: true,
      }).then((response) => {
        const result = JSON.parse(Crypter.decrypt(response.data)).Value;
        const rows = result?.StatutCode === 200 ? (result.Result?.Table ?? []) : [];
        const map: Record<string, string> = {};
        rows.forEach((row: any) => {
          const keys = Object.keys(row);
          map[row[keys[1]]] = row[keys[0]];
        });
        setLibellesParChamp((prev) => ({ ...prev, [champ]: map }));
      }).catch(() => {
        setLibellesParChamp((prev) => ({ ...prev, [champ]: {} }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Contexte "Type de signature" (opérateur modifié, ex. TSIGN2="&" — pas qui
  // occupe SIGN2/SIGN3) ET contexte ajout/retrait signataire/procuration (la
  // demande ne porte que le CODREL, pas sa photo/signature) : les deux ont
  // besoin de la liste des relations du client (noms, photo, signature).
  useEffect(() => {
    const toucheOperateur = detailsChampSelected.some((d) => String(d.Champ).startsWith('TSIGN'));
    const toucheRelation = detailsRelationSelected.length > 0;
    if (!selected || (!toucheOperateur && !toucheRelation)) {
      setCompteActuel(null);
      setRelationsClientActuel([]);
      return;
    }
    (async () => {
      try {
        const [compteRes, relRes] = await Promise.all([
          axios.get(Data.ApiUrl.GetCompte, {
            headers: { CypherNumCpte: Crypter.encrypt(selected.NUMCPT ?? ''), 'Content-Type': 'application/json' },
            withCredentials: true,
          }),
          axios.get(Data.ApiUrl.KycRelationsClient, {
            headers: { CypherNumcli: Crypter.encrypt(selected.NUMCLI ?? ''), 'Content-Type': 'application/json' },
            withCredentials: true,
          }),
        ]);
        const compteData = JSON.parse(Crypter.decrypt(compteRes.data));
        setCompteActuel(compteData?.Result?.Table?.[0] ?? null);
        const relData = JSON.parse(Crypter.decrypt(relRes.data));
        setRelationsClientActuel(relData?.StatutCode === 200 ? (relData?.Result?.Table ?? []) : []);
      } catch {
        setCompteActuel(null);
        setRelationsClientActuel([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Contexte ajout/retrait signataire/procuration : la demande ne porte que
  // la fiche concernée par l'action, pas la liste des autres personnes déjà
  // autorisées sur ce compte — on va la chercher pour donner ce contexte au
  // validateur, uniquement si la demande touche réellement une relation.
  useEffect(() => {
    if (!selected?.NUMCPT || detailsRelationSelected.length === 0) {
      setSignatairesActuelsCompte([]);
      setProcurationsActuelsCompte([]);
      return;
    }
    (async () => {
      try {
        const [signRes, procRes] = await Promise.all([
          axios.get(Data.ApiUrl.KycSignatairesCompte, {
            headers: { CypherNumcpt: Crypter.encrypt(selected.NUMCPT ?? ''), 'Content-Type': 'application/json' },
            withCredentials: true,
          }),
          axios.get(Data.ApiUrl.KycProcurationsCompte, {
            headers: { CypherNumcpt: Crypter.encrypt(selected.NUMCPT ?? ''), 'Content-Type': 'application/json' },
            withCredentials: true,
          }),
        ]);
        const signData = JSON.parse(Crypter.decrypt(signRes.data));
        setSignatairesActuelsCompte(signData?.StatutCode === 200 ? (signData?.Result?.Table ?? []) : []);
        const procData = JSON.parse(Crypter.decrypt(procRes.data));
        setProcurationsActuelsCompte(procData?.StatutCode === 200 ? (procData?.Result?.Table ?? []) : []);
      } catch {
        setSignatairesActuelsCompte([]);
        setProcurationsActuelsCompte([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Fiche client — uniquement pour le badge "Modifié X fois" (NbModifications-
  // Kyc), chargée pour toute demande sélectionnée (pas conditionné, contrairement
  // au contexte TSIGN ci-dessus).
  useEffect(() => {
    if (!selected?.NUMCLI) { setClientActuel(null); return; }
    axios.get(Data.ApiUrl.GetClient, {
      headers: { CypherNumcli: Crypter.encrypt(selected.NUMCLI), 'Content-Type': 'application/json' },
      withCredentials: true,
    }).then((response) => {
      const res = JSON.parse(Crypter.decrypt(response.data));
      setClientActuel(res?.StatutCode === 200 ? (res?.Result?.Table?.[0] ?? null) : null);
    }).catch(() => setClientActuel(null));
  }, [selected]);

  // "Qui" occupe l'emplacement SIGN<n> — SIGN1 est toujours la fiche du
  // client lui-même (règle backend), pas besoin de la chercher dans les
  // relations. Emplacement vide -> "— vide —" plutôt qu'un CODREL brut.
  function nomPourEmplacementSignature(n: number): string {
    if (n === 1) return selected?.NOMCLI ? `${selected.NOMCLI} (client)` : 'Signataire 1';
    const codRel = compteActuel?.[`Sign${n}`];
    if (!codRel) return '— vide —';
    const personne = relationsClientActuel.find((r) => r.CODREL === codRel);
    return personne ? `${personne.NomRel} ${personne.Prenom}`.trim() : codRel;
  }

  // PHOTO/SIGNATURE : la valeur est une data URI d'image potentiellement
  // volumineuse — jamais affichée en texte brut, toujours en aperçu image.
  // Pour un champ picklist, ajoute le libellé humain à côté du code. Couvre
  // les deux espaces de champs (KYC client/compte, ET identité signataire/
  // mandataire REL_*/PROC_*, 2026-10-08) — Champ est un string générique
  // côté API, pas garanti dans le seul union ChampKyc.
  const estChampImage = (champ: string) => CHAMPS_IMAGE_KYC.includes(champ as ChampKyc) || CHAMPS_IMAGE_IDENTITE_KYC.includes(champ as any);
  // Pièces jointes d'une procuration (PROC_PIECEJ1/2) : image OU PDF/Word —
  // contrairement à estChampImage, impossible de savoir à l'avance laquelle
  // sans regarder le MIME de la data URI elle-même (fait dans renderValeurChamp).
  const estChampDocument = (champ: string) => CHAMPS_DOCUMENT_IDENTITE_KYC.includes(champ as any);
  const labelChamp = (champ: string) => LABELS_CHAMP_KYC[champ as ChampKyc] ?? LABELS_CHAMP_IDENTITE_KYC[champ as keyof typeof LABELS_CHAMP_IDENTITE_KYC] ?? champ;
  const renderValeurChamp = (champ: string, valeur?: string | null) => {
    if (estChampImage(champ)) {
      return <KycImageThumb src={valeur} alt={labelChamp(champ)} size={60} />;
    }
    if (estChampDocument(champ)) {
      if (!valeur || valeur.length < LONGUEUR_MIN_DATA_URI) return '—';
      const mime = valeur.match(/^data:([^;]+);/)?.[1];
      if (mime?.startsWith('image/')) return <KycImageThumb src={valeur} alt={labelChamp(champ)} size={60} />;
      return (
        <Button size="small" icon={<VisibilityIcon style={{ fontSize: 14 }} />} onClick={() => setPreviewDocument({ label: labelChamp(champ), uri: valeur })}>
          Voir le document
        </Button>
      );
    }
    // Un opérateur ET/OU jamais renseigné vaut "/" par défaut en base — les
    // demandes créées avant ce correctif (2026-09-25) ont pu stocker "" au
    // lieu de "/", donc ce repli s'applique aussi à l'affichage, pas
    // seulement à la saisie côté KycPage.
    if (champ.startsWith('TSIGN') && !valeur) return '/';
    // "—" explicite plutôt que de renvoyer la valeur vide telle quelle : sans
    // ça, une cellule (voire toute une colonne si TOUTES ses valeurs sont
    // vides) ne rend rien du tout, et avec table-layout:auto le navigateur
    // peut alors réduire cette colonne à une largeur quasi nulle — invisible,
    // à tort pris pour "la colonne a disparu" plutôt que "valeur vide".
    if (!valeur) return '—';
    const libelle = libellesParChamp[champ]?.[valeur];
    return libelle ? `${valeur} — ${libelle}` : valeur;
  };

  const champTable = (rows: DemandeDetailChampRow[], texteVide: string) => (
    rows.length === 0 ? (
      <Typography color="text.secondary" sx={{ p: 2 }}>{texteVide}</Typography>
    ) : (
      <Table
        rowKey={(row: DemandeDetailChampRow) => `${row.IdDemande}-${row.Champ}`}
        size="small"
        bordered
        pagination={false}
        // Sans ça, une colonne Ancienne/Nouvelle valeur contenant une image
        // (Photo d'une procuration, par ex.) peut forcer le tableau à dépasser
        // la largeur disponible (panneau Actions + sidebar la réduisent déjà) —
        // le reste (dont "Nouvelle valeur") se retrouvait alors découpé au lieu
        // d'être scrollable horizontalement.
        scroll={{ x: 'max-content' }}
        dataSource={rows}
        columns={[
          {
            title: 'Champ', dataIndex: 'Champ', key: 'Champ',
            render: (v: string) => {
              const label = labelChamp(v);
              if (!v.startsWith('TSIGN')) return label;
              // Contextualise l'opérateur : quels signataires il relie (la
              // demande ne porte que l'opérateur, pas qui occupe chaque côté).
              const n = Number(v.replace('TSIGN', ''));
              const noms = [nomPourEmplacementSignature(n), nomPourEmplacementSignature(n + 1)].filter((nom) => nom !== '— vide —');
              return (
                <Box>
                  <Typography variant="body2">{label}</Typography>
                  {noms.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {noms.join(' ↔ ')}
                    </Typography>
                  )}
                </Box>
              );
            },
          },
          { title: 'Ancienne valeur', dataIndex: 'AncienneValeur', key: 'AncienneValeur', render: (v: string, row: DemandeDetailChampRow) => renderValeurChamp(row.Champ, v) },
          {
            title: 'Nouvelle valeur', dataIndex: 'NouvelleValeur', key: 'NouvelleValeur',
            render: (v: string, row: DemandeDetailChampRow) => (
              estChampImage(row.Champ) || estChampDocument(row.Champ)
                ? renderValeurChamp(row.Champ, v)
                : <Tag color="green">{renderValeurChamp(row.Champ, v)}</Tag>
            ),
          },
        ] as any}
      />
    )
  );

  // Carte "Action demandée" (ajout/retrait/modification signataire ou
  // procuration) — identique qu'on soit dans l'onglet "Compte" (KYC complet)
  // ou "Détails" (page dédiée identité), donc calculée une seule fois ici.
  const actionDemandeeBlock = relationSelectedRow ? (() => {
    // TypeRelation annoncé par le backend mais absent en pratique de la
    // réponse (vérifié sur demande n°43) : on ne peut pas savoir avec
    // certitude ajout/retrait ni signataire/procuration. On affiche un
    // libellé honnête plutôt qu'une étiquette fausse, avec une estimation
    // ajout/retrait basée sur la présence d'un Plafond (un retrait n'en a
    // pas besoin).
    const typeConnu = Boolean(relationInfo);
    const kind: 'ajout' | 'retrait' | 'modification' = typeConnu
      ? relationInfo!.kind
      : (relationSelectedRow.Plafond && relationSelectedRow.Plafond > 0 ? 'ajout' : 'retrait');
    const libelle = typeConnu
      ? relationInfo!.label
      : `${kind === 'ajout' ? 'Ajout' : 'Retrait'} signataire ou procuration (type exact non fourni par le backend)`;
    // La demande ne porte que le CODREL (pas sa photo/signature) —
    // recherchée dans les relations du client déjà chargées (même source que
    // pour le contexte "Type de signature" ci-dessus).
    const codRelPersonne = relationSelectedRow?.CodRel ?? relationSelectedRow?.NumRelExistant;
    const personneRelation = relationsClientActuel.find((r) => r.CODREL === codRelPersonne);
    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.3, fontSize: 12 }}>
          Action demandée
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          {kind === 'ajout' ? (
            <UserAddOutlined style={{ fontSize: 20, color: '#2e7d32' }} />
          ) : kind === 'retrait' ? (
            <UserDeleteOutlined style={{ fontSize: 20, color: '#c62828' }} />
          ) : (
            <EditOutlined style={{ fontSize: 20, color: '#1565c0' }} />
          )}
          <Tag color={typeConnu ? relationInfo!.color : 'default'} style={{ fontSize: 14, padding: '4px 10px' }}>
            {libelle}
          </Tag>
        </Stack>

        <Grid container spacing={1}>
          {infoTile('Nom', relationSelectedRow?.NomRel)}
          {infoTile('Prénom', relationSelectedRow?.Prenom)}
          {infoTile('CNI', relationSelectedRow?.Cni)}
          {Boolean(relationSelectedRow?.Plafond) && infoTile('Plafond', relationSelectedRow?.Plafond?.toLocaleString('fr-FR'))}
          {infoTile('Référence relation', codRelPersonne)}
          {infoTileImage('Photo', getVal(personneRelation, 'Photo', 'photo', 'PHOTO'))}
          {infoTileImage('Signature', getVal(personneRelation, 'Signature', 'signature', 'SIGNATURE'))}
        </Grid>
      </Box>
    );
  })() : null;

  // Page dédiée identité : un seul onglet "Détails" (même clé '1' que
  // "Client" en mode KYC complet, pour que le saut automatique après
  // sélection d'une demande — setSubTab('1') — continue de fonctionner sans
  // changement). Pas de tableaux "actuels du compte" ni de carte "Action
  // demandée" ici : ces demandes n'ont pas de NUMCPT (tableaux toujours
  // vides), et la carte (pensée pour un ajout/retrait sans diff de champs)
  // ne fait que répéter en moins précis ce que la table de diff montre déjà
  // (Nom/Prénom/CNI ne sont d'ailleurs pas modifiables séparément du diff).
  const detailsOuClientTab: NonNullable<TabsProps['items']>[number] = modeIdentite ? {
    key: '1',
    label: <span><IdcardOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Détails</span>,
    disabled: !selected,
    children: !selected ? (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        Sélectionnez une demande ci-dessous pour afficher le détail de la modification.
      </Typography>
    ) : (
      <Box sx={{ p: 1 }}>
        {champsCompteSelected.length > 0 ? champTable(champsCompteSelected, '') : (
          <Typography color="text.secondary">Aucune information pour cette demande.</Typography>
        )}
      </Box>
    ),
  } : {
    key: '1',
    label: <span><IdcardOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Client</span>,
    disabled: !selected,
    children: !selected ? (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        Sélectionnez une demande ci-dessous pour afficher les informations client à modifier.
      </Typography>
    ) : (
      <Box sx={{ p: 1 }}>{champTable(champsClientSelected, "Aucune modification d'information client pour cette demande.")}</Box>
    ),
  };

  const detailItems: TabsProps['items'] = [
    {
      key: '0',
      label: <span><GavelIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Demande</span>,
      children: (
        <Box sx={{ p: 1 }}>
          <SectionCard
            icon={<GavelIcon fontSize="small" />}
            title="Demandes"
            subtitle={sousTitre ?? 'Toutes les demandes, tous clients'}
            color="#5d4037"
          >
            {/* Même tableau que les écrans financement : TableauDossiers met sur
                une seule ligne la recherche globale, les filtres, le bouton
                réinitialiser et, à droite, le compteur + l'export. */}
            <TableauDossiers
              titreExport="Demandes de validation KYC"
              nomFichier="kyc_demandes"
              libelleElement="demande"
              filtres={FILTRES_DEMANDES}
              valeursFiltresDefaut={FILTRES_DEFAUT}
              pleineLargeur
              rowKey={(row: DemandeEnAttenteRow) => String(row.IdDemande)}
              rowSelection={{ ...rowSelection, type: 'radio' }}
              columns={demandeColumns as any}
              dataSource={demandesAffichees}
              loading={busy}
              size="small"
              locale={{ emptyText: 'Aucune demande.' }}
              scroll={{ x: 1700, y: 'calc(100vh - 340px)' }}
              pagination={{
                defaultPageSize: 20,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
                showTotal: (t) => `${t} demande(s)`,
              }}
            />
          </SectionCard>
        </Box>
      ),
    },
    detailsOuClientTab,
    ...(modeIdentite ? [] : [{
      key: '2',
      label: <span><CreditCardOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Compte</span>,
      disabled: !selected,
      // Une demande combinée peut porter à la fois une modification de champ
      // compte (Nom du compte) ET un ajout/retrait signataire/procuration :
      // les deux s'affichent l'un sous l'autre, ce n'est plus mutuellement
      // exclusif comme avant cette évolution backend.
      children: !selected ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          Sélectionnez une demande ci-dessous pour afficher ce qui a été modifié sur le compte.
        </Typography>
      ) : (
        <Box sx={{ p: 1 }}>
          {champsCompteSelected.length > 0 && (
            <Box sx={{ mb: relationSelectedRow ? 3 : 0 }}>{champTable(champsCompteSelected, '')}</Box>
          )}

          {relationSelectedRow && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              <SectionCard icon={<TeamOutlined style={{ fontSize: 14 }} />} title="Signataires actuels du compte" color="#2e7d32">
                <Table
                  rowKey="CODREL" size="small" bordered pagination={false}
                  dataSource={signatairesActuelsCompte}
                  locale={{ emptyText: 'Aucun signataire actuel sur ce compte.' }}
                  columns={[
                    { title: 'Nom', dataIndex: 'NomRel', key: 'NomRel' },
                    { title: 'Prénom', dataIndex: 'Prenom', key: 'Prenom' },
                    { title: 'CNI', dataIndex: 'cni', key: 'cni' },
                    {
                      title: 'Photo', key: 'photo', width: 60,
                      render: (_: any, row: SignataireCompteRow) => <KycImageThumb src={getVal(row, 'Photo', 'photo', 'PHOTO')} alt="Photo" />,
                    },
                    {
                      title: 'Signature', key: 'signature', width: 60,
                      render: (_: any, row: SignataireCompteRow) => <KycImageThumb src={getVal(row, 'Signature', 'signature', 'SIGNATURE')} alt="Signature" />,
                    },
                    { title: 'Plafond', dataIndex: 'LIMITE', key: 'LIMITE', render: (v: number) => v?.toLocaleString('fr-FR') },
                  ] as any}
                />
              </SectionCard>
              <SectionCard icon={<SafetyCertificateOutlined style={{ fontSize: 14 }} />} title="Procurations actuelles du compte" color="#e65100">
                <Table
                  rowKey="CODREL" size="small" bordered pagination={false}
                  dataSource={procurationsActuelsCompte}
                  locale={{ emptyText: 'Aucune procuration actuelle sur ce compte.' }}
                  columns={[
                    { title: 'Nom', dataIndex: 'nom', key: 'nom' },
                    { title: 'Prénom', dataIndex: 'prenom', key: 'prenom' },
                    { title: 'CNI', dataIndex: 'cni', key: 'cni' },
                    {
                      title: 'Photo', key: 'photo', width: 60,
                      render: (_: any, row: ProcurationCompteRow) => <KycImageThumb src={getVal(row, 'Photo', 'photo', 'PHOTO')} alt="Photo" />,
                    },
                    {
                      title: 'Signature', key: 'signature', width: 60,
                      render: (_: any, row: ProcurationCompteRow) => <KycImageThumb src={getVal(row, 'Signature', 'signature', 'SIGNATURE')} alt="Signature" />,
                    },
                    { title: 'Plafond', dataIndex: 'LIMITE', key: 'LIMITE', render: (v: number) => v?.toLocaleString('fr-FR') },
                  ] as any}
                />
              </SectionCard>
            </Stack>
          )}

          {actionDemandeeBlock}

          {champsCompteSelected.length === 0 && !relationSelectedRow && (
            <Typography color="text.secondary">Aucune modification du compte ni ajout signataire/procuration pour cette demande.</Typography>
          )}
        </Box>
      ),
    }]),
    {
      key: '4',
      label: <span><AttachFileIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Documents</span>,
      disabled: !selected,
      children: !selected ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          Sélectionnez une demande ci-dessous pour voir ses pièces jointes.
        </Typography>
      ) : (
        <Box sx={{ p: 1 }}>
          <KycDocumentsPanel idDemande={selected.IdDemande} uploadePar={user.coduti} />
        </Box>
      ),
    },
    {
      key: '5',
      label: <span><MessageOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />Commentaire</span>,
      disabled: !selected,
      children: !selected ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          Sélectionnez une demande ci-dessous pour rédiger un commentaire.
        </Typography>
      ) : dejaDecidee ? (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Commentaire enregistré avec la décision ({selected.StatutValidation === 'VALIDE' ? 'validée' : 'rejetée'} par {selected.ValidePar ?? '—'}{selected.DateValidation ? ` le ${selected.DateValidation}` : ''}) :
          </Typography>
          <Typography variant="body1">{selected.CommentaireValidation || '—'}</Typography>
        </Box>
      ) : (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Ce commentaire sera enregistré avec la décision (obligatoire pour un rejet).
          </Typography>
          <Input.TextArea
            rows={5}
            placeholder="Votre commentaire..."
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            style={{ maxWidth: 600, display: 'block' }}
          />
        </Box>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Spin spinning={busy} size="large" style={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              fontStyle="inherit"
              className="border"
              sx={{ p: 1, width: '100%', maxHeight: '75vh', overflowY: 'auto', boxSizing: 'border-box' }}
            >
            {selected && (
              <Box sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Demande n°{selected.IdDemande} — {selected.NUMCLI} — {selected.NOMCLI}
                  </Typography>
                  {getVal(clientActuel, 'NbModificationsKyc') !== undefined && (
                    <Chip size="small" color="info" variant="outlined" label={`Modifié ${getVal(clientActuel, 'NbModificationsKyc')} fois`} />
                  )}
                </Stack>
                {dejaDecidee && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selected.StatutValidation === 'VALIDE' ? 'Validée' : 'Rejetée'} par {selected.ValidePar ?? '—'}
                    {selected.DateValidation ? ` le ${selected.DateValidation}` : ''}
                    {selected.CommentaireValidation ? ` — Commentaire : ${selected.CommentaireValidation}` : ''}
                  </Typography>
                )}
              </Box>
            )}

            <AntTabs
              size="large" type="card" activeKey={subTab} items={detailItems}
              onChange={(key) => {
                // Filet de sécurité en plus de `disabled` sur chaque item :
                // Client/Compte/Documents/Commentaire restent verrouillés tant
                // qu'aucune demande n'est sélectionnée — mais "Demande" reste
                // toujours accessible pour changer de sélection à tout moment.
                if (key !== '0' && !selected) return;
                setSubTab(key);
              }}
            />
            </Box>
          </Box>

          <div
            className="bG"
            style={{
              flex: '0 0 220px', width: 220, height: 'auto',
              position: 'relative', left: 'auto', top: 'auto', marginTop: 0, boxSizing: 'border-box',
            }}
          >
            <legend><Typography color="darkgreen">Actions</Typography></legend>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!selected || dejaDecidee || estMaDemande(selected)} onClick={handleValidate}>
                Valider
              </Button>
              <Button
                size="large" className="bt_cancel" type="ghost" htmlType="button" block
                disabled={!selected || dejaDecidee || estMaDemande(selected)} onClick={handleReject}
                style={(!selected || dejaDecidee || estMaDemande(selected))
                  ? { backgroundColor: 'var(--colorOpaque060)', borderColor: 'var(--colorOpaque060)', color: '#fff' }
                  : { backgroundColor: '#a41b1b', borderColor: '#a41b1b', color: '#fff' }}
              >
                Rejeter
              </Button>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!selected} onClick={handleAnnuler}>
                Annuler
              </Button>
            </Space>
            {estMaDemande(selected) && (
              <Box
                role="alert"
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5, p: 1.25,
                  backgroundColor: '#fdecea', border: '1px solid #e57373', borderLeft: '5px solid #c62828',
                  borderRadius: 1, color: '#7f1d1d', boxShadow: '0 1px 4px rgba(198,40,40,.25)',
                }}
              >
                <ExclamationCircleFilled style={{ color: '#c62828', fontSize: 16, marginTop: 2 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                  {MSG_PROPRE_DEMANDE}
                </Typography>
              </Box>
            )}
          </div>
        </Box>
      </Spin>

      <ConfirmPasswordModal
        open={showPasswordConfirm}
        title={pendingDecision === 'VALIDE' ? 'Valider la demande' : 'Rejeter la demande'}
        onCancel={() => setShowPasswordConfirm(false)}
        onConfirm={onConfirmPassword}
      />

      {/* Même pattern que KycDocumentsPanel/EditableValueField : aperçu +
          bouton Télécharger, pas un lien qui ouvre un nouvel onglet brut. */}
      <Dialog open={Boolean(previewDocument)} onClose={() => setPreviewDocument(null)} maxWidth="md" fullWidth>
        <DialogTitle>{previewDocument?.label}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'grey.100', minHeight: 300 }}>
          {previewDocument && /pdf/i.test(previewDocument.uri.match(/^data:([^;]+);/)?.[1] ?? '') ? (
            <Box component="iframe" src={previewDocument.uri} title={previewDocument.label} sx={{ width: '100%', height: '70vh', border: 'none' }} />
          ) : (
            <Typography sx={{ alignSelf: 'center', textAlign: 'center' }} color="text.secondary">
              Aperçu non disponible pour ce type de fichier — téléchargez-le pour l'ouvrir.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            icon={<DownloadIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}
            href={previewDocument?.uri} download={previewDocument?.label}
            className="bt_add" style={{ color: '#fff' }}
          >
            Télécharger
          </Button>
          <Button onClick={() => setPreviewDocument(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
